/**
 * Keeps the cart's free-delivery bar honest.
 *
 * Two jobs, and each exists for a specific reason:
 *
 *  1. The reveal. When the drawer opens, the fill runs from empty to its real
 *     value so the bar reads as progress rather than as a static rule.
 *
 *  2. The catch-up. Horizon re-renders the drawer for its own cart mutations
 *     — the quantity steppers morph the section and the server-rendered bar
 *     comes back correct on its own. It does NOT re-render for a cart change
 *     made from anywhere else while the drawer sits open, so this recomputes
 *     from /cart.js whenever the cart reports a change.
 *
 * This lives in an asset rather than beside the markup on purpose: a script
 * inside the snippet is wiped by the first morph and never runs again.
 */
(function () {
  var CART_EVENT = 'shopify:cart:lines-update';

  /**
   * Renders cents through the shop's own money format, so the bar matches the
   * prices next to it rather than inventing its own.
   *
   * @param {number} cents
   * @param {string} format
   */
  function money(cents, format) {
    var value = (cents / 100).toFixed(2);
    var whole = value.split('.')[0];
    var withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + value.split('.')[1];
    var noDecimals = Math.round(cents / 100).toString();
    var noDecimalsCommas = noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return (format || '{{amount}}')
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimalsCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas);
  }

  function bar() {
    return document.querySelector('[data-cart-ship]');
  }

  /** Repaints the bar for a given cart total, in cents. */
  function paint(total) {
    var root = bar();
    if (!root) return;

    var threshold = parseInt(root.dataset.threshold, 10);
    if (!threshold || threshold <= 0) return;

    var remaining = threshold - total;
    var reached = remaining <= 0;
    var progress = reached ? 100 : Math.round((total * 100) / threshold);
    if (progress < 3) progress = 3;

    var fill = root.querySelector('[data-cart-ship-fill]');
    var text = root.querySelector('.cart-ship__text');
    var track = root.querySelector('[role="progressbar"]');

    if (fill) fill.style.width = progress + '%';
    if (track) track.setAttribute('aria-valuenow', String(progress));
    root.dataset.progress = String(progress);
    root.classList.toggle('cart-ship--reached', reached);

    if (text) {
      text.textContent = reached
        ? root.dataset.reachedText || 'Free delivery unlocked'
        : (root.dataset.awayText || '[amount] away from free delivery').replace(
            '[amount]',
            money(remaining, root.dataset.moneyFormat)
          );
    }

    /* The icon is a truck below the line and a tick above it. Swapping the
       whole SVG from script would mean keeping two copies of the artwork in
       sync, so only the state class moves and the icon is left to the next
       server render. */
  }

  function refresh() {
    if (!bar()) return;
    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (!cart) return;
        paint(cart.total_price);
      })
      .catch(function () {
        /* A failed poll leaves the server-rendered value in place, which is
           the right thing to fall back to. */
      });
  }

  /** Runs the fill from empty to its current value. */
  function reveal() {
    var root = bar();
    if (!root) return;

    var fill = root.querySelector('[data-cart-ship-fill]');
    if (!fill) return;

    var target = fill.style.width || root.dataset.progress + '%';
    fill.style.transition = 'none';
    fill.style.width = '0%';

    /* Two frames: one to commit the empty width without a transition, one to
       restore the transition and set the target. Collapsed into a single frame
       the browser coalesces both writes and nothing animates. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        fill.style.transition = '';
        fill.style.width = target;
      });
    });
  }

  document.addEventListener(CART_EVENT, function (event) {
    /* Horizon dispatches this when the update STARTS, not when it finishes,
       and hands the in-flight request along on the event so listeners can wait
       for it. Reading /cart.js on the event alone returns the old total. */
    var promise = event && event.detail && event.detail.promise;
    if (promise && typeof promise.then === 'function') {
      promise.then(refresh, refresh);
    }

    /* Not every path carries a promise, and a slow network can land after it
       resolves, so two spaced-out reads back it up. Each is one small JSON
       fetch, and landing on the same numbers twice repaints nothing. */
    setTimeout(refresh, 500);
    setTimeout(refresh, 1600);
  });

  /* Reveal whenever the drawer is opened, not just on page load — on load it
     would play behind a closed drawer and be missed. */
  function watchDrawer() {
    var dialog = document.querySelector('cart-drawer-component dialog, #cart-drawer');
    if (!dialog) return;

    new MutationObserver(function () {
      if (dialog.open) {
        refresh();
        reveal();
      }
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

    if (dialog.open) reveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawer);
  } else {
    watchDrawer();
  }
})();
